// WorkspaceDrawer — task-workspace prototype.
//
// Right-side drawer (same shell as InspectorDrawer) that surfaces the
// deck's R2 folder grouped by top-level prefix: artifacts/ (what the
// agent produced), memory/ (its working notes), context/ (user
// uploads), assets/ (brand bundles), etc. Each entry is clickable —
// click opens the shared InlineFileViewer below, which has the same
// read/edit affordance as the Inspector's file browser.
//
// Visible to admins AND deck owners. The backend route is auth-gated
// to match.

import React, { useCallback, useEffect, useState } from 'react';
import { X, ArrowsClockwise, Folder } from '@phosphor-icons/react';
import { apiFetch } from '../api/auth-fetch';
import InlineFileViewer, { type FileContent } from './InlineFileViewer';

interface WorkspaceDrawerProps {
  deckId: string;
  open: boolean;
  onClose: () => void;
  /** Snapshot prefetched by the parent rail. Lets the drawer paint
   *  with real data on the very first frame instead of a Loading…
   *  shimmer. Null is fine — the drawer will fetch on its own. */
  initialData?: WorkspaceData | null;
  /** Whether the parent rail's prefetch is still in flight. Drives
   *  the empty-state Loading text when initialData hasn't landed yet. */
  initialLoading?: boolean;
  /** Error from the parent rail's prefetch, if any. The drawer
   *  surfaces it instead of swallowing it. */
  initialError?: string;
  /** Hook for the refresh button — delegates back to the rail so the
   *  cached snapshot stays in sync with what the drawer shows. */
  onRefresh?: () => Promise<void> | void;
}

interface R2Item {
  key: string;
  size: number;
  uploaded: number;
}

export interface WorkspaceData {
  deck: Record<string, unknown>;
  prefix: string;
  total_size: number;
  object_count: number;
  groups: { name: string; count: number; total_size: number; items: R2Item[] }[];
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtTs(n: number): string {
  if (!n) return '-';
  const d = new Date(n);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Group display order: artifacts first (the headline output), then
// memory/context (agent state), then everything else alphabetically.
const GROUP_ORDER: Record<string, number> = {
  artifacts: 0,
  memory: 1,
  context: 2,
  assets: 3,
};

export default function WorkspaceDrawer({
  deckId, open, onClose,
  initialData = null, initialLoading = false, initialError = '',
  onRefresh,
}: WorkspaceDrawerProps) {
  // Seed local state with the parent's prefetched snapshot. We mirror
  // it into local state (instead of reading the prop directly each
  // render) so own-refresh + onSaved-driven refresh keep working.
  const [data, setData] = useState<WorkspaceData | null>(initialData);
  const [loading, setLoading] = useState(initialLoading);
  const [error, setError] = useState(initialError);

  // Sync if the prefetch lands while the drawer is open.
  useEffect(() => {
    if (initialData) setData(initialData);
  }, [initialData]);
  useEffect(() => { setLoading(initialLoading); }, [initialLoading]);
  useEffect(() => { setError(initialError); }, [initialError]);

  const [openFileKey, setOpenFileKey] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<FileContent | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [fileError, setFileError] = useState('');

  const openFile = useCallback(async (key: string) => {
    if (openFileKey === key) {
      setOpenFileKey(null);
      setFileContent(null);
      setFileError('');
      return;
    }
    setOpenFileKey(key);
    setFileContent(null);
    setFileError('');
    setFileLoading(true);
    try {
      const res = await apiFetch(`/api/decks/${deckId}/files?key=${encodeURIComponent(key)}`);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${text.slice(0, 200)}`);
      }
      const j = (await res.json()) as FileContent;
      setFileContent(j);
    } catch (e: any) {
      setFileError(e?.message || 'Failed to load file.');
    } finally {
      setFileLoading(false);
    }
  }, [deckId, openFileKey]);

  const load = useCallback(async () => {
    if (!deckId) return;
    // Delegate to the parent rail when it owns the cache — keeps the
    // prefetched snapshot and the drawer view in sync.
    if (onRefresh) {
      setLoading(true);
      setError('');
      try {
        await onRefresh();
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/decks/${deckId}/workspace`);
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${text.slice(0, 200)}`);
      }
      const j = (await res.json()) as WorkspaceData;
      setData(j);
    } catch (e: any) {
      setError(e?.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, [deckId, onRefresh]);

  // Standalone fallback: if no prefetch was supplied, fetch on first
  // open. With the rail wiring this branch is a no-op.
  useEffect(() => {
    if (open && !data && !loading && !onRefresh) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deckId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const backdrop: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 50,
    background: 'var(--ds-overlay)', backdropFilter: 'blur(2px)',
  };
  const panel: React.CSSProperties = {
    position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 51,
    width: 'min(720px, 92vw)',
    background: 'var(--bg-surface)',
    borderLeft: '1px solid var(--border)',
    boxShadow: '-12px 0 32px var(--ds-shadow-color)',
    display: 'flex', flexDirection: 'column',
  };
  const header: React.CSSProperties = {
    flexShrink: 0, padding: '14px 20px',
    borderBottom: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'var(--bg-surface)',
  };
  const body: React.CSSProperties = {
    flex: 1, overflow: 'auto', padding: '16px 20px',
  };
  const groupCard: React.CSSProperties = {
    background: 'var(--bg-primary)', border: '1px solid var(--border)',
    borderRadius: 8, padding: '12px 14px', marginBottom: 12,
  };
  const h2: React.CSSProperties = {
    fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-primary)',
    margin: '0 0 8px',
    display: 'flex', alignItems: 'center', gap: 8,
  };
  const btnIcon: React.CSSProperties = {
    background: 'none', border: 'none', cursor: 'pointer',
    padding: 6, borderRadius: 6, color: 'var(--text-muted)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  const sortedGroups = data
    ? [...data.groups].sort((a, b) => {
        const oa = GROUP_ORDER[a.name] ?? 100;
        const ob = GROUP_ORDER[b.name] ?? 100;
        if (oa !== ob) return oa - ob;
        return a.name.localeCompare(b.name);
      })
    : [];

  return (
    <>
      <div style={backdrop} onClick={onClose} />
      <aside style={panel} aria-label="Workspace">
        <div style={header}>
          <div>
            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Folder size={18} weight="light" />
              Workspace
            </div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginTop: 2, fontFamily: 'monospace' }}>
              {deckId}
              {data && (
                <span style={{ marginLeft: 8 }}>
                  · {data.object_count} obj · {fmtSize(data.total_size)}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => void load()} title="Refresh" style={btnIcon} disabled={loading}>
              <ArrowsClockwise size={16} weight="light" />
            </button>
            <button onClick={onClose} title="Close" style={btnIcon}>
              <X size={16} weight="light" />
            </button>
          </div>
        </div>
        <div style={body}>
          {loading && !data && (
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>Loading…</div>
          )}
          {error && (
            <div style={{
              padding: 10, background: 'var(--ds-danger-soft)',
              border: '1px solid var(--ds-danger-soft)', borderRadius: 6,
              color: 'var(--ds-danger)', fontSize: 'var(--fs-xs)', marginBottom: 12,
            }}>{error}</div>
          )}

          {data && sortedGroups.length === 0 && (
            <div style={{
              padding: 24, textAlign: 'center',
              border: '1px dashed var(--border)', borderRadius: 8,
              color: 'var(--text-muted)', fontSize: 'var(--fs-sm)',
            }}>
              <Folder size={24} weight="light" style={{ marginBottom: 8 }} />
              <div>The task workspace is empty.</div>
              <div style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>
                Files the agent creates will appear here as it works.
              </div>
            </div>
          )}

          {data && sortedGroups.map((g) => {
            const labelHints: Record<string, string> = {
              artifacts: 'agent output (slides, manifests, builds)',
              memory: 'agent working notes',
              context: 'user uploads',
              assets: 'brand bundles',
            };
            return (
              <div key={g.name} style={groupCard}>
                <h2 style={h2}>
                  <Folder size={14} weight="light" style={{ color: 'var(--text-secondary)' }} />
                  <span>{g.name}/</span>
                  <span style={{ fontWeight: 400, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
                    {g.count} · {fmtSize(g.total_size)}
                    {labelHints[g.name] && ` · ${labelHints[g.name]}`}
                  </span>
                </h2>
                <div style={{
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  borderRadius: 6, overflow: 'hidden',
                }}>
                  <table style={{ width: '100%', fontSize: 'var(--fs-2xs)', fontFamily: 'monospace', borderCollapse: 'collapse' }}>
                    <tbody>
                      {g.items.map((o) => {
                        const isOpen = openFileKey === o.key;
                        const shortKey = o.key.slice(`${data.prefix}${g.name}/`.length);
                        return (
                          <tr
                            key={o.key}
                            onClick={() => openFile(o.key)}
                            style={{ cursor: 'pointer', background: isOpen ? 'var(--bg-sidebar)' : undefined }}
                          >
                            <td style={{ padding: '4px 10px', borderTop: '1px solid var(--border)', color: isOpen ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                              {isOpen ? '▾ ' : '▸ '}{shortKey}
                            </td>
                            <td style={{ padding: '4px 10px', borderTop: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                              {fmtTs(o.uploaded)}
                            </td>
                            <td style={{ padding: '4px 10px', borderTop: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                              {fmtSize(o.size)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {openFileKey && (
            <div style={{ marginTop: 4 }}>
              <InlineFileViewer
                deckId={deckId}
                r2Key={openFileKey}
                onClose={() => { setOpenFileKey(null); setFileContent(null); setFileLoading(false); setFileError(''); }}
                content={fileContent}
                loading={fileLoading}
                error={fileError}
                onSaved={(newEtag, newSize) => {
                  setFileContent((c) => (
                    c && c.kind === 'text' ? { ...c, etag: newEtag, size: newSize } : c
                  ));
                  // Refresh group listing so the row reflects the new
                  // size + uploaded timestamp.
                  void load();
                }}
              />
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
